"use client";

import { useCallback, useState } from "react";
import type { PostDisplay } from "@/lib/posts/postQueries";
import type { PostListItem } from "@/lib/posts/postLists";
import {
  useDuplicateEventLink,
  usePostAnalyticsLink,
  usePostEditLink,
} from "@/lib/hooks/usePostLinks";
import { useUpdateBookmark } from "@/lib/hooks/useUpdateBookmark";
import { useUpdateReadStatus } from "@/lib/hooks/useUpdateReadStatus";
import { usePostSubscriptions } from "@/lib/hooks/useSubscriptions";
import {
  useApproveNewUser,
  useArchiveDraft,
  useExcludeFromRecommendations,
  useMoveToDraft,
  useMoveToFrontpage,
  useSetAsQuickTakesPost,
  useSuggestForCurated,
  useUnfeaturePost,
} from "@/lib/hooks/usePostModerationActions";
import clsx from "clsx";
import PencilIcon from "@heroicons/react/24/outline/PencilIcon";
import CalendarIcon from "@heroicons/react/24/outline/CalendarIcon";
import ChartBarIcon from "@heroicons/react/24/outline/ChartBarIcon";
import BellIcon from "@heroicons/react/24/outline/BellIcon";
import EllipsisVerticalIcon from "@heroicons/react/24/outline/EllipsisVerticalIcon";
import ArchiveBoxArrowDownIcon from "@heroicons/react/24/outline/ArchiveBoxArrowDownIcon";
import ArchiveBoxXMarkIcon from "@heroicons/react/24/outline/ArchiveBoxXMarkIcon";
import ArrowUturnLeftIcon from "@heroicons/react/24/outline/ArrowUturnLeftIcon";
import BookmarkSolidIcon from "@heroicons/react/24/solid/BookmarkIcon";
import BookmarkOutlineIcon from "@heroicons/react/24/outline/BookmarkIcon";
import ExclamationCircleIcon from "@heroicons/react/24/outline/ExclamationCircleIcon";
import EnvelopeIcon from "@heroicons/react/24/outline/EnvelopeIcon";
import EnvelopeOpenIcon from "@heroicons/react/24/outline/EnvelopeOpenIcon";
import CheckCircleIcon from "@heroicons/react/24/outline/CheckCircleIcon";
import XCircleIcon from "@heroicons/react/24/outline/XCircleIcon";
import StarIcon from "@heroicons/react/24/outline/StarIcon";
import StarSolidIcon from "@heroicons/react/24/solid/StarIcon";
import CheckIcon from "@heroicons/react/24/outline/CheckIcon";
import NewspaperIcon from "@heroicons/react/24/outline/NewspaperIcon";
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";
import ReportPopover from "../Moderation/ReportPopover";
import DropdownMenu from "../Dropdown/DropdownMenu";
import Tooltip from "../Tooltip";
import Type from "../Type";

export default function PostTripleDotMenu({
  post,
  hideBookmark,
  withBackground,
  onArchivedChange,
  className,
}: Readonly<{
  post: PostDisplay | PostListItem;
  hideBookmark?: boolean;
  withBackground?: boolean;
  onArchivedChange?: (archived: boolean) => void;
  className?: string;
}>) {
  const [reportOpen, setReportOpen] = useState(false);
  const openReport = useCallback(() => setReportOpen(true), []);
  const closeReport = useCallback(() => setReportOpen(false), []);
  const { subscriptionMenuItems } = usePostSubscriptions(post);
  const editLink = usePostEditLink(post);
  const duplicateEventLink = useDuplicateEventLink(post);
  const analyticsLink = usePostAnalyticsLink(post);
  const { isBookmarked, toggleIsBookmarked } = useUpdateBookmark(
    "Posts",
    post._id,
    post.bookmarks?.[0]?.active ?? false,
  );
  const { isRead, toggleIsRead } = useUpdateReadStatus(
    post._id,
    !!post.readStatus?.[0]?.isRead,
  );
  const { hasSuggestedForCuration, toggleSuggestedForCuration } =
    useSuggestForCurated(post);
  const { excludedFromRecommendations, toggleExcludeFromRecommendations } =
    useExcludeFromRecommendations(post);
  const { isFrontpage, toggleFrontpage } = useMoveToFrontpage(post);
  const { archived, toggleArchived } = useArchiveDraft(post, onArchivedChange);
  const setAsQuickTakesPost = useSetAsQuickTakesPost(post);
  const unfeaturePost = useUnfeaturePost(post);
  const moveToDraft = useMoveToDraft(post);
  const approveNewUser = useApproveNewUser(post);

  // TODO: Remaining actions from PostActions.tsx - do we need these?
  //  - resync rss
  //  - hide from frontpage
  //  - edit tags

  return (
    <>
      <DropdownMenu
        pageElementContext="tripleDotMenu"
        placement="bottom-end"
        className="text-gray-900"
        items={[
          editLink
            ? {
                title: "Edit",
                Icon: PencilIcon,
                href: editLink,
              }
            : null,
          duplicateEventLink
            ? {
                title: "Duplicate event",
                Icon: CalendarIcon,
                href: duplicateEventLink,
              }
            : null,
          analyticsLink
            ? {
                title: "Analytics",
                Icon: ChartBarIcon,
                href: analyticsLink,
              }
            : null,
          subscriptionMenuItems.length
            ? {
                title: "Get notified",
                Icon: BellIcon,
                submenu: subscriptionMenuItems,
              }
            : null,
          hideBookmark
            ? null
            : {
                title: isBookmarked ? "Saved" : "Save",
                Icon: isBookmarked ? BookmarkSolidIcon : BookmarkOutlineIcon,
                onClick: toggleIsBookmarked,
              },
          {
            title: "Report",
            Icon: ExclamationCircleIcon,
            onClick: openReport,
          },
          {
            title: isRead ? "Mark as unread" : "Mark as read",
            Icon: isRead ? EnvelopeIcon : EnvelopeOpenIcon,
            onClick: toggleIsRead,
          },
          toggleSuggestedForCuration
            ? {
                title: hasSuggestedForCuration
                  ? "Unsuggest curation"
                  : "Suggest curation",
                Icon: hasSuggestedForCuration ? StarSolidIcon : StarIcon,
                onClick: toggleSuggestedForCuration,
              }
            : null,
          moveToDraft
            ? {
                title: "Move to draft",
                Icon: ArchiveBoxArrowDownIcon,
                onClick: moveToDraft,
              }
            : null,
          toggleArchived
            ? {
                title: archived ? "Unarchive draft" : "Archive draft",
                Icon: archived ? ArrowUturnLeftIcon : ArchiveBoxXMarkIcon,
                onClick: toggleArchived,
              }
            : null,
          toggleExcludeFromRecommendations
            ? {
                title: excludedFromRecommendations
                  ? "Enable recommendation"
                  : "Disable recommendation",
                Icon: excludedFromRecommendations ? CheckCircleIcon : XCircleIcon,
                onClick: toggleExcludeFromRecommendations,
              }
            : null,
          approveNewUser
            ? {
                title: "Approve new user",
                Icon: CheckIcon,
                onClick: approveNewUser,
              }
            : null,
          toggleFrontpage
            ? {
                title: isFrontpage ? "Move to personal blog" : "Move to frontpage",
                Icon: NewspaperIcon,
                onClick: toggleFrontpage,
              }
            : null,
          unfeaturePost
            ? {
                title: "Unfeature post",
                Icon: XMarkIcon,
                onClick: unfeaturePost,
              }
            : null,
          setAsQuickTakesPost
            ? {
                title: "Set as user's quick takes post",
                onClick: setAsQuickTakesPost,
              }
            : null,
        ]}
      >
        <Tooltip title={<Type style="bodySmall">More actions</Type>} offsetPx={8}>
          <button
            aria-label="More actions"
            className={clsx(
              "cursor-pointer flex items-center text-gray-600",
              withBackground
                ? "rounded p-1 hover:bg-item-hover hover:text-gray-800"
                : "hover:text-gray-1000",
            )}
          >
            <EllipsisVerticalIcon
              className={clsx(withBackground ? "w-6" : "w-5", className)}
            />
          </button>
        </Tooltip>
      </DropdownMenu>
      <ReportPopover post={post} open={reportOpen} onClose={closeReport} />
    </>
  );
}
